import { useEffect, useState } from 'react';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import { buildBackupData, downloadBackupZip } from '@/lib/backup';
import { exportProductsToExcel, exportProductsToPdf } from '@/lib/productExport';
import { formatDateTime } from '@/lib/format';

interface BackupRow {
  id: string;
  user_name: string | null;
  type: string;
  branch_name: string | null;
  size_bytes: number | null;
  status: string;
  created_at: string;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function BackupPage() {
  const { profile } = useAuth();
  const { branches, activeBranchId } = useBranch();
  const [scope, setScope] = useState<string>('full');
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [history, setHistory] = useState<BackupRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const userName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.email || 'Administrador';

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await supabase
        .from('backups')
        .select('id, user_name, type, branch_name, size_bytes, status, created_at')
        .order('created_at', { ascending: false })
        .limit(50);
      setHistory((data as BackupRow[]) ?? []);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);
    const branchId = scope === 'full' ? null : scope;
    const branchName = branchId ? branches.find((b) => b.id === branchId)?.name ?? null : null;

    try {
      const result = await buildBackupData({ branchId, branchName });
      const size = await downloadBackupZip(result);

      const { error: logError } = await supabase.from('backups').insert({
        user_id: profile?.id ?? null,
        user_name: userName,
        type: branchId ? 'sucursal' : 'completo',
        branch_id: branchId,
        branch_name: branchName,
        size_bytes: size,
        status: 'completado',
        detail: { tables: result.meta.tables },
      });
      if (logError) throw logError;

      setSuccess(
        branchId
          ? `Copia de seguridad de ${branchName} creada y descargada.`
          : 'Copia de seguridad completa creada y descargada.'
      );
      loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la copia de seguridad');
    } finally {
      setCreating(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting('excel');
    setError(null);
    setSuccess(null);
    try {
      const count = await exportProductsToExcel(activeBranchId);
      setSuccess(`Se descargaron ${count} productos en Excel.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo exportar a Excel');
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    setExporting('pdf');
    setError(null);
    setSuccess(null);
    try {
      const count = await exportProductsToPdf(activeBranchId);
      setSuccess(`Se descargaron ${count} productos en PDF.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo exportar a PDF');
    } finally {
      setExporting(null);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div className="max-w-4xl">
      <SettingsBreadcrumb label="Copias de seguridad" icon="ri-archive-line" />
      <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Copias de seguridad
      </h1>
      <p className="mt-2 text-foreground-600">
        Crea y descarga un respaldo de toda la información del sistema o solo de una sucursal.
      </p>

      {/* Crear backup */}
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading font-semibold text-foreground-950">Crear backup</h2>
        <div className="mt-4 max-w-md">
          <label className={labelClass}>Alcance del respaldo</label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className={inputClass}>
            <option value="full">Backup completo (todas las sucursales)</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                Solo sucursal: {b.name}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 flex items-center gap-2 text-sm text-foreground-700">
            <i className="ri-checkbox-circle-line text-accent-700" /> {success}
          </div>
        )}

        <button
          onClick={handleCreate}
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
        >
          {creating ? (
            <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
          ) : (
            <i className="ri-download-cloud-line" />
          )}
          {creating ? 'Generando...' : 'Crear y descargar backup'}
        </button>
        <p className="mt-2 text-xs text-foreground-500">
          Se descargará un archivo .zip con la información en formato JSON, listo para conservar.
        </p>
      </div>

      {/* Descargar productos */}
      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
          <i className="ri-box-3-line text-primary-700" /> Descargar productos
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          Exporta únicamente el listado de productos con su precio, stock y estado. No incluye
          ventas, clientes ni otra información.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleExportExcel}
            disabled={exporting !== null}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {exporting === 'excel' ? (
              <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
            ) : (
              <i className="ri-file-excel-2-line" />
            )}
            {exporting === 'excel' ? 'Generando...' : 'Descargar en Excel'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exporting !== null}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {exporting === 'pdf' ? (
              <div className="w-4 h-4 rounded-full border-2 border-foreground-500 border-t-transparent animate-spin" />
            ) : (
              <i className="ri-file-pdf-2-line" />
            )}
            {exporting === 'pdf' ? 'Generando...' : 'Descargar en PDF'}
          </button>
        </div>
        {activeBranchId && (
          <p className="mt-2 text-xs text-foreground-500">
            El stock mostrado corresponde a la sucursal activa.
          </p>
        )}
      </div>

      {/* Restauración (próximamente) */}
      <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
        <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
          <i className="ri-restart-line text-secondary-700" /> Restaurar backup
        </h2>
        <p className="mt-2 text-sm text-foreground-700">
          Restaurar un backup puede reemplazar información existente. Asegúrate de crear un backup
          actual antes de continuar.
        </p>
        <button
          disabled
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-400 cursor-not-allowed"
        >
          <i className="ri-time-line" /> Disponible próximamente
        </button>
      </div>

      {/* Historial */}
      <div className="mt-8">
        <h2 className="font-heading text-lg font-semibold text-foreground-950">Historial de backups</h2>
        {loadingHistory ? (
          <div className="mt-6 flex items-center justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-background-300 p-10 text-center">
            <i className="ri-database-2-line text-3xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-600">Aún no se han creado copias de seguridad.</p>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-background-100 text-foreground-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Fecha y hora</th>
                  <th className="px-4 py-3 font-medium">Tipo</th>
                  <th className="px-4 py-3 font-medium">Sucursal</th>
                  <th className="px-4 py-3 font-medium">Usuario</th>
                  <th className="px-4 py-3 font-medium text-right">Tamaño</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {history.map((row) => (
                  <tr key={row.id} className="hover:bg-background-100 transition">
                    <td className="px-4 py-3 text-foreground-700">{formatDateTime(row.created_at)}</td>
                    <td className="px-4 py-3 text-foreground-900">
                      {row.type === 'completo' ? 'Backup completo' : 'Por sucursal'}
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{row.branch_name || 'Todas'}</td>
                    <td className="px-4 py-3 text-foreground-700">{row.user_name || '—'}</td>
                    <td className="px-4 py-3 text-right text-foreground-700">{formatSize(row.size_bytes)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-900">
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}